import React, { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_THRESHOLD = 50; // 端から何pxで反応するか
const MAX_SCROLL_SPEED = 15; // 最大スクロール速度

export const useAutoScroll = (scrollContainerRef: React.RefObject<HTMLElement>) => {
  const [isDragging, setIsDragging] = useState(false);
  const animationFrameRef = useRef<number>();
  const lastMouseYRef = useRef<number>(0);

  const handleAutoScroll = useCallback(() => {
    if (!isDragging || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseY = lastMouseYRef.current;

    let scrollSpeed = 0;

    // 上端に近い場合
    if (mouseY < rect.top + SCROLL_THRESHOLD) {
      const distance = rect.top + SCROLL_THRESHOLD - mouseY;
      scrollSpeed = -Math.min(distance / SCROLL_THRESHOLD * MAX_SCROLL_SPEED, MAX_SCROLL_SPEED);
    }
    // 下端に近い場合
    else if (mouseY > rect.bottom - SCROLL_THRESHOLD) {
      const distance = mouseY - (rect.bottom - SCROLL_THRESHOLD);
      scrollSpeed = Math.min(distance / SCROLL_THRESHOLD * MAX_SCROLL_SPEED, MAX_SCROLL_SPEED);
    }

    if (scrollSpeed !== 0) {
      container.scrollTop += scrollSpeed;
    }

    animationFrameRef.current = requestAnimationFrame(handleAutoScroll);
  }, [isDragging, scrollContainerRef]);

  useEffect(() => {
    if (isDragging) {
      animationFrameRef.current = requestAnimationFrame(handleAutoScroll);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, handleAutoScroll]);

  const onDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    lastMouseYRef.current = e.clientY;
  }, []);

  return { onDragStart, onDragEnd, onDragOver };
}